<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, inject } from "vue";
import type { Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import type { AuthTokens } from "aws-amplify/auth";
import { useBookingData } from "@/stores/booking-storage";
import { isMobile } from "@/utilities/IsMobileHelper";
import NavigationList from "@/components/Layout/NavigationList.vue";
import InfoBanner from "@/components/InfoBanner.vue";
import type { RetailInstanceTypeService } from "@/services/RetailInstanceTypeService";
import { retailInstanceTypeSymbol } from "@/models/Symbols";
import { doesCurrentAuthenticatedUserHaveOnAccountPaymentEnabled } from "@/utilities/doesCurrentAuthenticatedUserHaveOnAccountPaymentEnabled";
import { isUserLoggedIn } from "@/utilities/isUserLoggedIn";

const emit = defineEmits(["banner-visibility-changed"]);
const route = useRoute();
const $router = useRouter();
const isHidden: Ref<boolean> = ref(false);
const isLoggedIn: Ref<boolean> = ref(false);
const userEmail: Ref<string> = ref("");
const basketCount: Ref<number> = ref(0);
const bookingStore = useBookingData();
const showMobileNavDrawer: Ref<boolean> = ref(false);
const dinersBenefits = ref<string[]>([]); // Data for the banner list - typed as an array of strings
const showInfoBanner: Ref<boolean> = ref(false);
const notificationBannerHeight = ref(0); // Height of the notification banner
const infoBannerHeightBreakpoints = [
    { breakpoint: 1080, height: 80 },
    { breakpoint: 640, height: 100 },
    { breakpoint: 480, height: 160 },
    { breakpoint: 355, height: 170 },
    { breakpoint: 320, height: 210 },
    { breakpoint: 0, height: 230 },
];
const whiteListedDomains = ref(["conicalsphere.com", "mytrainticket.co.uk", "dinersclub.co.uk", "affiniture.onmicrosoft.com", "sky.com"]);
const instanceTypeSvc = inject(retailInstanceTypeSymbol) as RetailInstanceTypeService;

function updateBasketCount() {
    bookingStore.checkExistingBooking();
    basketCount.value = bookingStore.bookingData?.trips.length ? bookingStore.bookingData!.trips.length : 0;
}

function getMainSiteUrl() {
    if (window.location.hostname.includes("retail-widget.test.rocketonrail.co.uk") || window.location.hostname.includes("corporate-widget.test.rocketonrail.co.uk")) {
        return "https://test.mytrainticket.co.uk";
    }
    return "https://mytrainticket.co.uk";
}

const updateBannerHeightForResolution = () => {
    const windowWidth = window.innerWidth;
    let newHeight: number = 60; // Default to 'auto' or your smallest defined height

    // Find the appropriate height for the current window width
    // Assuming breakpoints are ordered (e.g., largest to smallest)
    for (const bp of infoBannerHeightBreakpoints) {
        if (windowWidth >= bp.breakpoint) {
            newHeight = bp.height;
            break; // Found the matching breakpoint
        }
    }

    if (notificationBannerHeight.value !== newHeight) {
        notificationBannerHeight.value = newHeight;
    }
};

watch(bookingStore, () => {
    updateBasketCount();
});

onUnmounted(() => {
    window.removeEventListener("resize", updateBannerHeightForResolution);
});

function userEmailInDomainWhitelist(emailAddress: string): boolean {
    if (!emailAddress || typeof emailAddress !== "string" || !emailAddress.includes("@")) {
        return false;
    }
    const parts = emailAddress.split("@");

    if (parts.length !== 2) {
        return false; // Invalid email format
    }
    const emailDomain = parts[1].toLowerCase();

    if (!emailDomain) {
        return false; // No valid domain extracted from the email
    }
    // Check if the extracted userDomain is present in the whiteListedDomains array
    return whiteListedDomains.value.includes(emailDomain);
}

async function showInfoBannerIfAppropriate() {
    try {
        const currentlyLoggedIn = await isUserLoggedIn();
        if (!currentlyLoggedIn) {
            showInfoBanner.value = false;
            return;
        }

        if (!instanceTypeSvc.isCorporate()) {
            showInfoBanner.value = false;
            return;
        }

        const userHasOnAccountPaymentEnabled = await doesCurrentAuthenticatedUserHaveOnAccountPaymentEnabled();

        if (userEmailInDomainWhitelist(userEmail.value) && instanceTypeSvc.isCorporate() && !userHasOnAccountPaymentEnabled) {
            showInfoBanner.value = true;
        } else {
            showInfoBanner.value = false;
        }
    } catch (e) {
        console.error("Error in showInfoBannerIfAppropriate:", e);
        showInfoBanner.value = false;
    }
}

onMounted(async () => {
    await $router.isReady();
    isHidden.value = "hide-nav" in route.query && route.query["hide-nav"] === "true";
    const session = await fetchAuthSession();
    if (session.tokens !== undefined) {
        // if we're logged in
        isLoggedIn.value = true;
        const details = await getCurrentUser();
        userEmail.value = details.signInDetails?.loginId || "";
    }
    updateBasketCount();

    showInfoBannerIfAppropriate();

    // Set intial height for the notification banner
    updateBannerHeightForResolution();
    window.addEventListener("resize", updateBannerHeightForResolution);
});

Hub.listen("auth", (ev) => {
    showInfoBannerIfAppropriate();
    const { payload } = ev;
    switch (payload.event) {
        case "signedIn":
            isLoggedIn.value = true;
            userEmail.value = (payload.data! as unknown as AuthTokens)!.signInDetails!.loginId!;
            break;
        case "signedOut":
            isLoggedIn.value = false;
            break;
        default:
            break;
    }
});
</script>

<template>
    <!-- Main App Bar -->
    <v-app-bar elevation="2" name="main-app-bar" order="-1" app>
        <v-app-bar-title>
            <a :href="getMainSiteUrl()">
                <img src="@/assets/logo.svg" alt="MyTrainTicket" class="logo" />
            </a>
        </v-app-bar-title>

        <template v-if="!isMobile()">
            <NavigationList :basket-count="basketCount" :is-logged-in="isLoggedIn"></NavigationList>
        </template>

        <template v-slot:append>
            <v-app-bar-nav-icon @click="showMobileNavDrawer = !showMobileNavDrawer" v-if="isMobile()"></v-app-bar-nav-icon>
        </template>
    </v-app-bar>

    <v-app-bar v-if="showInfoBanner" name="notification-banner" order="0" app :height="notificationBannerHeight">
        <!-- Info Banner -->
        <InfoBanner
            title="Looking to simplify Train Bookings? Get up to 52 Days Credit with a Diners Club International Statement Account"
            subtitle=""
            :list-items="dinersBenefits"
            cta-text="LEARN MORE"
            cta-url="https://mytrainticket.co.uk/diners-club-international-statement-accounts/"
            subtext="Exclusive offer for MyTrainTicket travellers"
            @closed="
                showInfoBanner = false;
                emit('banner-visibility-changed');
            "
            class="banner-style"
        >
            <template #icon>
                <img src="https://cdn.mytrainticket.co.uk/wp-content/icons/cc-diners-club-brands_white.svg" alt="" width="45" height="45" />
            </template>
        </InfoBanner>
    </v-app-bar>

    <v-navigation-drawer v-model="showMobileNavDrawer" location="top" v-if="isMobile()">
        <NavigationList :basket-count="basketCount" :is-logged-in="isLoggedIn"></NavigationList>
    </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.v-app-bar-title .logo {
    height: 31px;
    vertical-align: middle;
}

.nav-item-active .v-list-item-title {
    font-weight: bold;
}

.banner-style {
    width: 100%;
}
</style>
